import React, { useState, useEffect } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Graticule,
  Marker,
  Sphere,
} from "react-simple-maps";
import { fetchAllBusiness } from "../../../services/BusinessService";
import formatError from "../../../services/FormatError";
import { countryCoordinates, countryToCode } from "../../../utils/mapData";
import { useTranslation } from "react-i18next";

const geoUrl = "/features.json";

const DynamicMapChart = () => {
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [business, setBusiness] = useState([]);
  const [tooltip, setTooltip] = useState({
    visible: false,
    content: "",
    x: 0,
    y: 0,
  });
  const { t } = useTranslation();

  const fetchMapData = async () => {
    try {
      setLoading(true);
      const res = await fetchAllBusiness(1, 100);
      const response = res.data?.result?.data ?? [];
      setBusiness(response);
      setMapData(response);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.error || error.message);
      formatError(error.response?.data?.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMapData();
  }, []);

  const processedData = mapData.map((item) => ({
    id: item.id,
    name: countryToCode[item.country] || item.country,
    country: item.country,
    city: item.city,
    region: item.region,
    coordinates: countryCoordinates[item.country] || [0, 0],
    count: 1,
  }));

  const countryStats = processedData.reduce((acc, item) => {
    const countryCode = countryToCode[item.country];
    if (countryCode) {
      acc[countryCode] = (acc[countryCode] || 0) + 1;
    }
    return acc;
  }, {});

  const highlightedCountries = Object.keys(countryStats);

  const handleMouseEnter = (event, geo) => {
    const isHighlighted = highlightedCountries.includes(geo.id);
    const count = countryStats[geo.id] || 0;

    let content = geo.properties.name;
    if (isHighlighted) {
      content += ` (${count} location${count > 1 ? "s" : ""})`;
    } else {
      content += " (No data)";
    }

    setTooltip({
      visible: true,
      content: content,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleMouseMove = (event) => {
    if (tooltip.visible) {
      setTooltip((prev) => ({
        ...prev,
        x: event.clientX,
        y: event.clientY,
      }));
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, content: "", x: 0, y: 0 });
  };

  if (loading) {
    return (
      <div
        style={{
          width: "100%",
          height: "600px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "18px",
          color: "#666",
        }}
      >
        {t("loadingMapData")}
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          width: "100%",
          height: "600px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "18px",
          color: "#e74c3c",
        }}
      >
        {t("errorLoadingMapData")} {error}
        <button
          onClick={fetchMapData}
          style={{
            marginLeft: "10px",
            padding: "8px 16px",
            backgroundColor: "#3498db",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          {t("retry")}
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: "600px", position: "relative" }}>
      <div
        style={{
          marginBottom: "10px",
          padding: "10px",
          backgroundColor: "#f8f9fa",
          borderRadius: "4px",
        }}
      >
        <strong>{t("dataSummary")}</strong> {mapData.length}{" "}
        {t("locationsLoaded")}
        <button
          onClick={fetchMapData}
          style={{
            marginLeft: "15px",
            padding: "6px 12px",
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            borderRadius: "3px",
            cursor: "pointer",
            fontSize: "12px",
          }}
        >
          {t("refreshData")}
        </button>
      </div>

      <ComposableMap
        projection="geoEqualEarth"
        style={{ width: "100%", height: "calc(100% - 60px)" }}
        onMouseMove={handleMouseMove}
      >
        <Sphere stroke="#DDD" />
        <Graticule stroke="#DDD" />
        <Geographies geography={geoUrl} stroke="#EE4E4E" strokeWidth={0.5}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const isHighlighted = highlightedCountries.includes(geo.id);
              const count = countryStats[geo.id] || 0;

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={isHighlighted ? "#DDE6ED" : "#31363F10"}
                  style={{
                    hover: {
                      fill: isHighlighted ? "#B8C5D1" : "#31363F30",
                      cursor: "pointer",
                    },
                  }}
                  onMouseEnter={(event) => handleMouseEnter(event, geo)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => {
                    if (isHighlighted) {
                      const countryData = mapData.filter(
                        (item) => countryToCode[item.country] === geo.id
                      );
                      console.log(
                        `${geo.properties.name} - ${count} location(s):`,
                        countryData
                      );
                    } else {
                      console.log(geo.properties.name, "- No data");
                    }
                  }}
                />
              );
            })
          }
        </Geographies>

        {processedData.map(({ country, coordinates, id }) => {
          const count = countryStats[countryToCode[country]] || 0;
          if (count === 0 || !countryToCode[country]) return null;

          return (
            <Marker key={`${country}-${id}`} coordinates={coordinates}>
              <circle
                r={Math.max(4, Math.min(15, count * 7))}
                fill="#e74c3c"
                opacity={0.8}
              />
              <text
                textAnchor="middle"
                y={2}
                style={{
                  fontFamily: "system-ui",
                  fill: "white",
                  fontSize: "12px",
                  fontWeight: "bold",
                  pointerEvents: "none",
                }}
              >
                {count}
              </text>
              <text
                textAnchor="middle"
                y={15}
                style={{
                  fontFamily: "system-ui",
                  fill: "#333",
                  fontSize: "8px",
                  fontWeight: "600",
                  pointerEvents: "none",
                }}
              >
                {country}
              </text>
            </Marker>
          );
        })}
      </ComposableMap>

      {/* Tooltip */}
      {tooltip.visible && (
        <div
          style={{
            position: "fixed",
            left: tooltip.x - 200,
            top: tooltip.y - 100,
            backgroundColor: "#333",
            color: "white",
            padding: "8px 12px",
            borderRadius: "4px",
            fontSize: "14px",
            fontWeight: "500",
            pointerEvents: "none",
            zIndex: 1000,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            whiteSpace: "nowrap",
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};

export default DynamicMapChart;
